import type { MutableRefObject } from 'react';
import { acquireTokenSilent } from '@/lib/msal';
import { protectedResources } from '@/lib/msalConfig';
import { MIC_CAPTURE_MAX_RETRIES, MIC_CAPTURE_RETRY_DELAY_MS } from '../constants';
import type { VoiceLiveContext, VoiceLivePublicEvent } from '../machine/types';
import type { VoiceLiveServerMessage } from '../types';
import { buildVoiceLiveWsUrl } from '../utils';
import { routeServerMessage } from './serverMessageRouter';
import type { UseAvatarPeerConnectionResult } from './useAvatarPeerConnection';
import type { UseConversationTranscriptResult } from './useConversationTranscript';
import type { UseTokenUsageResult } from './useTokenUsage';
import type { UseVoiceLiveAudioResult } from './useVoiceLiveAudio';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

/** Subsystem APIs the machine's effect actions drive (held in a ref by the hook). */
export interface MachineDeps {
  telemetry: UseVoiceLiveTelemetryResult;
  audio: UseVoiceLiveAudioResult;
  transcript: UseConversationTranscriptResult;
  tokenUsage: UseTokenUsageResult;
  peer: UseAvatarPeerConnectionResult;
}

const MIC_PERMISSION_ERROR_NAMES = new Set([
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'SecurityError',
  'OverconstrainedError',
]);

export const detachWebSocketHandlers = (ws: WebSocket): void => {
  ws.onopen = null;
  ws.onclose = null;
  ws.onerror = null;
  ws.onmessage = null;
};

export interface OpenVoiceLiveSocketOptions {
  context: VoiceLiveContext;
  deps: MachineDeps;
  wsRef: MutableRefObject<WebSocket | null>;
  send: (event: VoiceLivePublicEvent) => void;
  /** Read fresh at message time — the socket outlives any single snapshot. */
  getContext: () => VoiceLiveContext;
  onSessionCreated?: (sessionId: number) => void;
}

/**
 * Replaces any live socket, resets the audio pipeline, and opens a new authenticated
 * Voice Live WebSocket, translating its frames into machine events.
 */
export const openVoiceLiveSocket = ({
  context,
  deps,
  wsRef,
  send,
  getContext,
  onSessionCreated,
}: OpenVoiceLiveSocketOptions): void => {
  const { audio, telemetry, transcript, tokenUsage, peer } = deps;
  // The socket is audio-only iff video is off (the machine keeps
  // `socketIsAudioOnly` in lockstep on every connecting entry).
  const audioMode = !context.isVideoEnabled;
  const isReconnect = context.fieldOneSessionId != null;

  telemetry.markConnectStart();

  // Close any previous socket without forking another reconnect chain.
  if (wsRef.current) {
    const oldWs = wsRef.current;
    detachWebSocketHandlers(oldWs);
    wsRef.current = null;
    oldWs.close();
  }
  audio.teardownAnalysers();
  audio.closeContext();
  audio.stopCapture();
  if (isReconnect) {
    peer.cleanupAvatar();
  }
  audio.createContext();

  const backendUrl = process.env.NEXT_PUBLIC_VITE_BACKEND_URL || 'http://localhost:8000';
  const { wsUrl, host } = buildVoiceLiveWsUrl({
    backendUrl,
    userId: context.userId,
    scenarioId: context.scenarioId,
    sessionId: context.fieldOneSessionId,
    isReconnect,
    audioMode,
    usageRunId: telemetry.voiceLiveSessionId(),
    usageRunStartedAt: telemetry.usageRunStartedAt(),
  });
  telemetry.setBackendHost(host);
  telemetry.trackDiagnostic('Backend', 'ConnectStart', { isReconnect, userId: context.userId });

  const ws = new WebSocket(wsUrl);
  wsRef.current = ws;
  const isCurrentOpenSocket = () => wsRef.current === ws && ws.readyState === WebSocket.OPEN;

  ws.onopen = async () => {
    let token: string | null;
    try {
      token = await acquireTokenSilent(protectedResources.backendApi.scopes);
    } catch {
      if (isCurrentOpenSocket()) {
        ws.close(4000, 'Auth token acquisition failed');
      }
      return;
    }

    // Stop/reconnect can supersede this socket while auth is pending.
    // Never authenticate or start capture against a replacement context.
    if (!isCurrentOpenSocket()) return;
    if (!token) {
      ws.close(4000, 'Failed to acquire auth token');
      return;
    }

    ws.send(JSON.stringify({ type: 'auth', token }));
    send({ type: 'WS_OPEN' });
    transcript.ensureStartTime();
    if (isReconnect && transcript.hasTurns()) {
      ws.send(JSON.stringify(transcript.buildRestorePayload()));
    }

    for (let attempt = 0; attempt <= MIC_CAPTURE_MAX_RETRIES; attempt += 1) {
      if (!isCurrentOpenSocket()) return;

      try {
        await audio.startCapture((base64Audio) => {
          if (isCurrentOpenSocket()) {
            ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: base64Audio }));
          }
        });
        return;
      } catch (error) {
        if (!isCurrentOpenSocket()) return;

        const isErrorLike = error instanceof DOMException || error instanceof Error;
        const errorName = isErrorLike ? error.name : 'Unknown';
        if (attempt < MIC_CAPTURE_MAX_RETRIES) {
          telemetry.trackDiagnostic('Browser', 'MicCaptureRetry', {
            attempt: attempt + 1,
            errorName,
            userId: context.userId,
          });
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, MIC_CAPTURE_RETRY_DELAY_MS);
          });
          continue;
        }

        const detail = isErrorLike ? error.message : String(error);
        const isPermissionError =
          error instanceof DOMException && MIC_PERMISSION_ERROR_NAMES.has(error.name);
        telemetry.trackDiagnostic('Browser', 'MicCaptureFailed', {
          errorName,
          userId: context.userId,
        });

        if (isPermissionError) {
          alert(
            `Failed to access microphone: ${detail}\n\nPlease:\n1. Check that this site has microphone permission in your browser\n2. Make sure a microphone is connected and not in use by another app\n3. Allow microphone access when prompted, then try again`,
          );
        }

        send({
          type: 'MIC_CAPTURE_FAILED',
          message: isPermissionError
            ? ''
            : `Could not start the microphone: ${detail}. Please try again.`,
        });
        return;
      }
    }
  };

  ws.onmessage = (event) => {
    if (wsRef.current !== ws) return;
    const message: VoiceLiveServerMessage = JSON.parse(event.data);
    routeServerMessage(message, {
      audio,
      transcript,
      tokenUsage,
      telemetry,
      send,
      socketIsAudioOnly: getContext().socketIsAudioOnly,
      onSessionCreated,
    });
  };

  ws.onerror = () => {
    if (wsRef.current === ws) {
      send({ type: 'WS_ERROR' });
    }
  };
  ws.onclose = (event) => {
    if (wsRef.current === ws) {
      wsRef.current = null;
      // `reason` and `wasClean` never reach the machine, so they ride their own diagnostic.
      telemetry.trackDiagnostic('Backend', 'SocketClosed', {
        code: event.code,
        elapsedMs: telemetry.elapsedMs(),
        reason: event.reason,
        wasClean: event.wasClean,
      });
      send({ type: 'WS_CLOSE', code: event.code });
    }
  };
};
